import React, { useState, useEffect } from "react";
import { DashboardLayout } from "components/layout/components/DashboardLayout";
import { Navbar } from "components/layout/components/Navbar";
import { Input, Select, Button, Form, DatePicker } from "antd";
import { HomeOutlined, RightOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { routePath } from "config/routeMgt/routePath";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import dayjs from "dayjs";
import { pdfjs } from "react-pdf";
import { openNotification } from "utils/notification";
import { generalValidationRules } from "utils/formHelpers/validations";
import {
  LEVEL_LIST,
  WORK_MODELS,
  COUNTRIES,
  EMPLOYMENT_TYPES,
  LANGUAGES,
} from "constants/general";
import { useCreateJob } from "../hooks/useCreateJob";
import { QUERY_KEY_FOR_JOBS, useGetSectors } from "../hooks/useGetSectors";
import { END_POINT } from "config/environment";
import { IJobsProps } from "../types";
import { useQueryClient } from "react-query";
import { AppButton } from "components/button/AppButton";
import { useGetSingleJob } from "../hooks/useGetSingleJob";
import WarningModal from "components/layout/components/WarningModal";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
const CITIES_API_URL = "https://countriesnow.space/api/v0.1/countries/cities";

export const JobsEdit = () => {
  const [form] = Form.useForm();
  const { token, userInfo } = useGetUserInfo();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [isCancelModalVisible, setCancelModalVisible] = useState(false);
  const { mutate, isLoading } = useCreateJob();

  const params = useParams();
  const id = params.id as any;

  const { data, isSuccess, refetch } = useGetSingleJob({
    token,
    id: id as any,
  });

  const { data: sectorData, isLoading: loadingSector } = useGetSectors();

  const [filteredCities, setFilteredCities] = useState<any[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [language, setLanguage] = useState<string | null>(null);
  const [jobTitles, setJobTitles] = useState<
    { label: string; value: string }[]
  >([]);

  const [isJobCreatedModalVisible, setJobCreatedModalVisible] = useState(false);
  const [createdJobId, setCreatedJobId] = useState<number | null>(null);
  const [createdJobStatus, setCreatedJobStatus] = useState<string | null>(null);

  const isEdit = true;


  const fetchCities = async (countryCode: string) => {
    try {
      const response = await fetch(CITIES_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          country: countryCode,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setFilteredCities(data.data);
      } else {
        console.error("Failed to fetch cities:", data);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  const fetchJobTitles = async () => {
    try {
      const response = await fetch(`${END_POINT.BASE_URL}/v1/jobtitles`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (response.ok) {
        const titles = data.map((jobTitle: { name: string }) => ({
          label: jobTitle.name,
          value: jobTitle.name,
        }));
        setJobTitles(titles);
      }
    } catch (error) {
      console.error("Error fetching job titles:", error);
    }
  };

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
    const country = COUNTRIES.find((country) => country.name === value);
    if (country) {
      fetchCities(country.name);
    }
  };

  const handleLanguageChange = (value: string) => {
    setLanguage(value);
  };

  useEffect(() => {
    if (isSuccess) {
      const expiry_date = dayjs(data.expiry_date);
      form.setFieldsValue({
        title: data.title,
        company_name: data.company_name,
        type: data.type,
        employment_type: data.employment_type,
        level: data.level,
        expiryDate: expiry_date,
        experience: data.years_of_experience,
        description: data.description,
        language: data.language,
        location_city: data.location_city,
        location_country: data.location_country,
        skills: data.skills,
        sector_id: data.sector_id,
        status: data.status,
      });
    } else {
      form.resetFields();
    }
  }, [form, id, data, isSuccess]);

  useEffect(() => {
    fetchJobTitles();
  }, []);

  const disablePastDates = (current: dayjs.Dayjs) => {
    return current && current.isBefore(dayjs().endOf("day"));
  };

  const navigate = useNavigate();

  const handleSubmit = (mydata: any) => {
    const jobTitle = mydata.title === "others" ? mydata.customTitle : mydata.title;
    const skills = data?.skills ? data.skills.map((item: any) => item.name) : [];
    const status = "draft";
    const props: IJobsProps = {
      recruiter_id: userInfo?.recruiter?.id,
      token,
      userInfo,
      title: jobTitle,
      sector_id: mydata.sector_id,
      company_name: mydata.company_name,
      employment_type: mydata.employment_type,
      type: mydata.type,
      level: mydata.level,
      language: mydata.language,
      expiry_date: mydata.expiryDate.format("YYYY-MM-DD"),
      years_of_experience: mydata.experience,
      description: data?.description ? data?.description : "",
      location_id: 1,
      id: id as unknown as any,
      location_city: mydata.location_city,
      location_country: mydata.location_country,
      skills,
      status: data?.status ? data.status : status,
    };
    mutate(props, {
      onError: (err: any) => {
        let errorTitle = "Error";
        let errorDescription = "An error occurred, please try again.";

        if (err.response && err.response.data && err.response.data.error) {
          errorTitle = "Error: " + err.response.data.error;
          errorDescription =
            err.response.data.description || "Please try again.";
        }

        openNotification({
          title: errorTitle,
          state: "error",
          description: errorDescription,
          duration: 6,
        });
      },
      onSuccess: (res: any) => {
        setCreatedJobId(res.data.data.id);
        setJobCreatedModalVisible(true);
        setCreatedJobStatus(status);
        form.resetFields();
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY_FOR_JOBS] });
        navigate(routePath.jobs);
      },
    });
  };
  useEffect(() => {
    if (!isOpen && id) {
      refetch();
      setIsOpen(true)
    }
  }, [isOpen, id]);


  return (
    <DashboardLayout>
      <Navbar
        title="Add Job"
        description="View and Manage Jobs"
      />

      <div>
        <h3 className="text-sm flex items-center gap-1 text-gray-700">
          <HomeOutlined className="text-gray-500" />
          <span>Jobs&nbsp;</span>
          <RightOutlined className="text-xs" />
          <span>&nbsp;Add Job</span>
        </h3>
      </div>

      <div className="flex justify-center items-center mt-8">
        <div className="flex items-center">
          <div className="flex items-center justify-center w-6 h-6 rounded-full border border-gray-500 text-gray-500 text-sm font-semibold">
            1
          </div>
          <div className="w-60 h-1 bg-blue-950"></div>
          <div className="flex items-center justify-center w-6 h-6 rounded-full border border-gray-500 text-gray-500 text-sm font-semibold">
            <span className="cursor-not-allowed">2</span>
          </div>
        </div>
      </div>


      <div className="bg-white shadow-md rounded-md p-3 mt-6 w-full mx-auto">
        <div className="grid gap-4">
          <Form
            form={form}
            onFinish={handleSubmit}
            requiredMark="optional"
            layout="vertical"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 mt-5"
          >
            <Form.Item
              rules={generalValidationRules}
              name="title"
              data-tour="job-title"
              label="Job Title"
              className="md:col-span-2"
            >
              <Select
                options={[...jobTitles, { label: "Others", value: "others" }]}
                showSearch
                placeholder="Select"
                allowClear
                loading={jobTitles.length === 0}
                filterOption={(input, option) => {
                  if (option?.value === "others") {
                    return true;
                  }
                  return (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase());
                }}
                onChange={(value) => {
                  if (value !== "others") {
                    form.setFieldsValue({ customTitle: undefined });
                  }
                }}
                className="h-9 mb-3"
                disabled={isEdit}
              />
            </Form.Item>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 col-span-4 gap-y-3 md:gap-y-0 gap-x-3 -mt-3">
              <Form.Item
                shouldUpdate={(prev, cur) => prev.title !== cur.title}
                noStyle
              >
                {({ getFieldValue }) => {
                  const selectedTitle = getFieldValue("title");
                  return !isEdit && selectedTitle === "others" ? (
                    <Form.Item
                      name="customTitle"
                      label="Custom Job Title"
                      className="md:col-span-2"
                    >
                      <Input disabled={isEdit} />
                    </Form.Item>
                  ) : null;
                }}
              </Form.Item>

              <Form.Item
                rules={generalValidationRules}
                name="company_name"
                data-tour="company-name"
                label="Company Name"
              >
                <Input disabled={isEdit} />
              </Form.Item>

              <Form.Item
                rules={generalValidationRules}
                name="sector_id"
                label="Sector"
                data-tour="sector"
              >
                <Select
                  allowClear
                  showSearch
                  placeholder="Select"
                  loading={loadingSector}
                  options={sectorData?.map((sector) => ({
                    value: sector.id,
                    label: sector.name,
                  }))}
                />
              </Form.Item>

              <Form.Item
                rules={generalValidationRules}
                name="location_country"
                data-tour="country"
                label="Country"
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select"
                  options={
                    COUNTRIES?.length
                      ? COUNTRIES.map((country) => ({
                        value: country.name,
                        label: country.name,
                      }))
                      : []
                  }
                  onChange={handleCountryChange}
                />
              </Form.Item>

              <Form.Item
                shouldUpdate={(prevValues, curValues) =>
                  prevValues.location_country !== curValues.location_country
                }
                noStyle
              >
                {({ getFieldValue }) => (
                  <Form.Item
                    rules={generalValidationRules}
                    name="location_city"
                    label="Location (City)"
                    data-tour="city"
                  >
                    <Select
                      showSearch
                      options={filteredCities.map((city) => ({
                        value: city,
                        label: city,
                      }))}
                      allowClear
                      placeholder="Select"
                      disabled={!getFieldValue("location_country")}
                    />
                  </Form.Item>
                )}
              </Form.Item>

              <Form.Item
                rules={generalValidationRules}
                data-tour="experience-level"
                name="level"
                label="Experience Level"
              >
                <Select
                  options={LEVEL_LIST}
                  placeholder="Select"
                  allowClear
                />
              </Form.Item>

              <Form.Item
                rules={generalValidationRules}
                name="type"
                data-tour="work-model"
                label="Work Model"
              >
                <Select
                  options={WORK_MODELS}
                  placeholder="Select"
                  allowClear
                />
              </Form.Item>

              <Form.Item
                rules={generalValidationRules}
                data-tour="employment-type"
                name="employment_type"
                label="Employment Type"
              >
                <Select
                  options={EMPLOYMENT_TYPES}
                  placeholder="Select"
                  allowClear
                />
              </Form.Item>
              <Form.Item
                rules={generalValidationRules}
                name="experience"
                data-tour="years-of-experience"
                label="Years of Experience"
                normalize={(v) =>
                  typeof v === "string" ? v.replace(/[^0-9\-+ ]/g, "") : v ?? ""
                }
              >
                <Input
                  className="w-full"
                  placeholder="3 - 5"
                />
              </Form.Item>
              <Form.Item
                name="language"
                label="Preferred Language"
                rules={
                  !isEdit
                    ? [
                      {
                        required: true,
                        message: "Please select a preferred language",
                      },
                    ]
                    : []
                }
              >
                <Select
                  showSearch
                  options={LANGUAGES}
                  placeholder="Select preferred language"
                  onChange={handleLanguageChange}
                  allowClear
                />
              </Form.Item>

              <Form.Item
                name="expiryDate"
                label="Job Post Expiry Date"
                data-tour="expiry-date"
                rules={generalValidationRules}
              >
                <DatePicker
                  disabledDate={disablePastDates}
                  className="w-full"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </div>

            <div className="col-span-4 flex justify-between items-center mt-4">
              <Button onClick={() => setCancelModalVisible(true)} className="px-8">Cancel</Button>
              <div>
                <AppButton label="Save" type="submit" isLoading={isLoading} />
              </div>
            </div>

          </Form>
        </div>

        <WarningModal
          open={isCancelModalVisible}
          title="Cancel progress"
          content={<>
            Once cancelled, progress would not be saved.
          </>}
          onConfirm={() => navigate(-1)}
          onCancel={() => setCancelModalVisible(false)}
          confirmText="Cancel"
          cancelText="Back"
        />
      </div><p><br /><br /><br /></p>
    </DashboardLayout>
  );
};
